import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useEnvAddresses } from '@/hooks/useEnvAddresses';

import { formatUnits } from 'viem';
import { formatTokenAmount } from '@/utils/formatTokenAmount';
import { fetchRaceById } from '@/services/raceService';

import RaceTracks from '@/components/watch/RaceTracks';
import RaceTimer from '@/components/watch/watchDetail/RaceTimer/RaceTimer';
import RaceHeader from '@/components/watch/watchDetail/RaceHeader/RaceHeader';
import PlayByPlay from '@/components/watch/watchDetail/PlayByPlay/PlayByPlay';
import RaceStandings from '@/components/watch/watchDetail/RaceStandings/RaceStandings';
import ParticipantSection from '@/components/watch/watchDetail/ParticipantSection/ParticipantSection';

import './styles.css';

const Index = () => {
  const { id = '' } = useParams();
  const { usdc } = useEnvAddresses();
  const navigate = useNavigate();

  const {
    data: race,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['race', id],
    queryFn: () => fetchRaceById(id),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading races...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg text-red-500">Failed to load races</div>
      </div>
    );
  }

  if (!race) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg text-red-500">Race not found</div>
      </div>
    );
  }

  // Sort tokens by price change percentage
  const tokensByPriceChange = [...race.tokenStats].sort((a, b) => {
    return b.token.score - a.token.score;
  });

  return (
    <div className="watch-container">
      <div className="watch-detail">
        <div className="watch-detail-container">
          <button
            onClick={() => {
              navigate('/watch');
            }}
            className="back-button-races mb-6"
          >
            ← Back to Races
          </button>
          <div className="flex gap-4">
            <div className="race-main-content">
              <RaceHeader race={race} />
              <div className="race-content-section">
                <RaceTimer raceEndTime={race.raceEndTime} status={race.status} />
                <div className="flex">
                  <RaceStandings
                    tokenStats={tokensByPriceChange}
                    raceStatus={race.status}
                    oracleStartTimestamp={race.oracleStartTimestamp}
                    oracleEndTimestamp={race.oracleEndTimestamp}
                    handicapEnabled={race.handicapEnabled}
                  />
                  <div className="relative flex-1">
                    <RaceTracks
                      tokens={tokensByPriceChange}
                      raceStartTime={race.raceStartTime}
                      raceEndTime={race.raceEndTime}
                      trackClassName="right-2 bottom-0 w-3"
                      horseImageClassName="w-20 h-20"
                      tokenClassName="right-10 top-6"
                      tokenIconClassName="w-5 h-5"
                      tokenSymbolClassName="text-sm"
                      horsePosition={{ first: 'mt-24', rest: 'mt-32' }}
                      compact={false}
                    />
                  </div>
                </div>
                {/* {race.concluded && race.winner && (
                  <RaceEnded hasRaceEnded={race.concluded} winningToken={race.winner} />
                )} */}
              </div>
              <PlayByPlay />
            </div>
            <div className="side-panel">
              <div className="panel-section">
                <h3 className="panel-section-title">Race Statistics</h3>
                <div className="stats-grid">
                  <div className="stat-item">
                    <span className="stat-label">Total Staking Reward Pool</span>
                    <span className="stat-value">${formatTokenAmount(race.totalPool, usdc.decimals, 'usdc')}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Race Creator Edge</span>
                    <span className="stat-value">
                      {formatUnits(race.creatorFeePercent, 18)}% ( $
                      {formatTokenAmount(
                        (race.totalPool * race.creatorFeePercent) / BigInt(1e18),
                        usdc.decimals,
                        'usdc'
                      )}{' '}
                      )
                    </span>
                  </div>
                </div>
                <ParticipantSection race={race} tokensByPriceChange={tokensByPriceChange} />
              </div>
            </div>
          </div>
        </div>
        {/* {race.concluded && race.winner && (
          <RaceEndedModal
            isOpen={isRaceEndedModalShow}
            onClose={() => setIsRaceEndedModalShow(false)}
            winningToken={race.winner}
            tokensStats={tokensByPriceChange}
            totalParticipants={race.totalParticipants}
          />
        )} */}
      </div>
    </div>
  );
};

export default Index;
